import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ArrowRightLeft, Clock, CheckCircle, XCircle, DollarSign, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending_review: { label: 'PENDING', color: 'text-zinc-400', bg: 'bg-zinc-500/20', icon: Clock },
  interested: { label: 'INTERESTED', color: 'text-teal-400', bg: 'bg-teal-500/20', icon: CheckCircle },
  offer_made: { label: 'OFFER RECEIVED', color: 'text-yellow-400', bg: 'bg-yellow-500/20', icon: DollarSign },
  accepted: { label: 'ACCEPTED', color: 'text-green-400', bg: 'bg-green-500/20', icon: CheckCircle },
  declined: { label: 'DECLINED', color: 'text-red-400', bg: 'bg-red-500/20', icon: XCircle }
};

// Image Lightbox with swipe/carousel support
const ImageLightbox = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentIndex < images.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const goNext = (e) => {
    e.stopPropagation();
    if (currentIndex < images.length - 1) setCurrentIndex(prev => prev + 1);
  };

  const goPrev = (e) => {
    e.stopPropagation();
    if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && currentIndex < images.length - 1) setCurrentIndex(prev => prev + 1);
      if (e.key === 'ArrowLeft' && currentIndex > 0) setCurrentIndex(prev => prev - 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, images.length, onClose]);
  
  if (!images || images.length === 0) return null;
  
  const getImageUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    return `${API_URL}${img}`;
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <button onClick={onClose} className="absolute top-4 right-4 p-2 text-white z-10 bg-zinc-800/50 hover:bg-zinc-700">
        <X size={24} />
      </button>

      <div className="absolute top-4 left-4 text-white font-mono text-sm bg-zinc-800/50 px-3 py-1">
        {currentIndex + 1} / {images.length}
      </div>

      {images.length > 1 && (
        <>
          {currentIndex > 0 && (
            <button onClick={goPrev} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-zinc-800/50 text-white hover:bg-zinc-700 z-10">
              <ChevronLeft size={24} />
            </button>
          )}
          {currentIndex < images.length - 1 && (
            <button onClick={goNext} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-zinc-800/50 text-white hover:bg-zinc-700 z-10">
              <ChevronRight size={24} />
            </button>
          )}
        </>
      )}

      <div
        className="w-full h-full flex items-center justify-center p-4"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <img 
          src={getImageUrl(images[currentIndex])}
          alt=""
          className="max-w-full max-h-full object-contain"
        />
      </div>

      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentIndex ? 'bg-teal-500' : 'bg-zinc-600 hover:bg-zinc-500'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const MySubmissionsSection = ({ isInternational = false }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [lightboxData, setLightboxData] = useState(null); // { images: [], initialIndex: 0 }
  const [responding, setResponding] = useState(null);

  const getToken = () => {
    return localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token');
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/submissions/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
      }
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const respondToOffer = async (submissionId, response, itemId = null) => {
    setResponding(`${submissionId}-${itemId || 'main'}`);
    const token = getToken();

    try {
      let url = `${API_URL}/api/submissions/${submissionId}/respond?response=${response}`;
      if (itemId) url += `&item_id=${itemId}`;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success(`OFFER ${response.toUpperCase()}ED`);
        fetchSubmissions();
      } else {
        const data = await res.json();
        toast.error(data.detail?.toUpperCase() || 'FAILED');
      }
    } catch (err) {
      toast.error('FAILED TO RESPOND');
    } finally {
      setResponding(null);
    }
  };

  const getImageUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    return `${API_URL}${img}`;
  };

  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending_review;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-none text-xs font-mono ${config.bg} ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="border border-zinc-800 rounded-none p-4">
        <h3 className="font-mono text-sm text-teal-500 mb-3">MY SUBMISSIONS</h3>
        <p className="text-zinc-500 text-xs font-mono">LOADING...</p>
      </div>
    );
  }

  return (
    <div className="border border-zinc-800 rounded-none p-4">
      {/* Lightbox */}
      {lightboxData && (
        <ImageLightbox
          images={lightboxData.images}
          initialIndex={lightboxData.initialIndex}
          onClose={() => setLightboxData(null)}
        />
      )}

      <div className="flex items-center justify-between mb-3">
        <h3 className="font-mono text-sm text-teal-500">MY SUBMISSIONS</h3>
        {!isInternational && (
          <Link 
            to="/submit-items"
            className="flex items-center gap-1 text-xs text-teal-400 font-mono"
          >
            <Plus size={14} /> SELL
          </Link>
        )}
      </div>

      {submissions.length === 0 ? (
        <div className="text-center py-6">
          <Package className="mx-auto mb-2 text-zinc-600" size={28} />
          <p className="text-zinc-500 text-xs mb-2 font-mono">NO SUBMISSIONS YET</p>
          {!isInternational && (
            <Link 
              to="/submit-items"
              className="inline-block px-3 py-1.5 bg-teal-500 text-black text-xs font-bold font-mono"
            >
              SELL ITEMS
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {submissions.map(sub => (
            <div key={sub.id} className="border border-zinc-700 rounded-none overflow-hidden">
              {/* Header */}
              <div 
                className="p-2 flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedSubmission(expandedSubmission === sub.id ? null : sub.id)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`p-1.5 rounded-none ${sub.type === 'trade' ? 'bg-purple-500/20 text-purple-400' : 'bg-teal-500/20 text-teal-400'}`}>
                    {sub.type === 'trade' ? <ArrowRightLeft size={14} /> : <Package size={14} />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-mono uppercase truncate">
                      {sub.type === 'trade' 
                        ? `TRADE: ${sub.product_name}`
                        : `${sub.items?.length} ITEM${sub.items?.length !== 1 ? 'S' : ''}`
                      }
                    </p>
                    <p className="text-[10px] text-zinc-500 font-mono">{sub.id}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {sub.type === 'trade' ? renderStatusBadge(sub.status) : null}
                  {sub.type === 'trade' && sub.status === 'accepted' && (
                    <span
                      className={`px-2 py-1 text-[10px] font-mono font-bold border ${sub.trade_received ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'}`}
                      data-testid={`my-trade-receipt-${sub.id}`}
                      title={sub.trade_received_at ? new Date(sub.trade_received_at).toLocaleString() : ''}
                    >
                      {sub.trade_received
                        ? `✓ RECEIVED ${sub.trade_received_at ? '· ' + new Date(sub.trade_received_at).toLocaleDateString(undefined,{month:'short',day:'numeric'}) : ''}`
                        : 'PENDING RECEIVING'}
                    </span>
                  )}
                  {expandedSubmission === sub.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </div>
              </div>

              {/* Expanded */}
              {expandedSubmission === sub.id && (
                <div className="border-t border-zinc-700 p-2 space-y-2">
                  {sub.type === 'trade' ? (
                    /* Trade Offer */
                    <div className="space-y-2">
                      {/* What they wanted */}
                      <div className="bg-teal-500/10 border border-teal-500/30 p-2 flex items-center gap-3">
                        {sub.product_image && (
                          <img 
                            src={getImageUrl(sub.product_image)} 
                            alt="" 
                            className="w-14 h-14 object-cover flex-shrink-0"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] text-teal-400 font-mono mb-1">YOU WANTED</p>
                          <p className="text-sm font-mono uppercase truncate">{sub.product_name}</p>
                          <p className="text-xs text-zinc-400 font-mono">${(sub.product_price ?? 0).toFixed(0)}</p>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-1 text-center text-xs font-mono">
                        <div className="bg-zinc-800 p-1.5">
                          <p className="text-zinc-500">ASKING</p>
                          <p>${(sub.product_price ?? 0).toFixed(0)}</p>
                        </div>
                        <div className="bg-zinc-800 p-1.5">
                          <p className="text-zinc-500">YOUR OFFER</p>
                          <p className="text-teal-400">${(sub.total_offer_value ?? 0).toFixed(0)}</p>
                        </div>
                        <div className="bg-zinc-800 p-1.5">
                          <p className="text-zinc-500">+ CASH</p>
                          <p className="text-green-400">${(sub.cash_offer ?? 0).toFixed(0)}</p>
                        </div>
                      </div>

                      {/* Your trade item */}
                      <div className="bg-purple-500/10 border border-purple-500/30 p-2">
                        <p className="text-[10px] text-purple-400 font-mono mb-1">YOUR TRADE ITEM (${(sub.item?.estimated_value ?? 0).toFixed(0)})</p>
                        <p className="text-sm font-mono uppercase">{sub.item?.name || 'UNNAMED ITEM'}</p>
                        {sub.item?.description && sub.item?.description !== '-' && (
                          <p className="text-[10px] text-zinc-400 font-mono">{sub.item?.description}</p>
                        )}
                        {sub.item?.images?.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {sub.item.images.map((img, idx) => (
                              <img 
                                key={idx}
                                src={getImageUrl(img)}
                                alt=""
                                className="w-10 h-10 object-cover cursor-pointer hover:opacity-80"
                                onClick={() => setLightboxData({ images: sub.item.images, initialIndex: idx })}
                              />
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Counter Offer from Admin */}
                      {sub.counter_offer && (
                        <div className="bg-yellow-500/20 border border-yellow-500/50 p-2">
                          <p className="text-xs text-yellow-400 font-mono">THEIR COUNTER OFFER</p>
                          <p className="text-lg font-bold font-mono">${sub.counter_offer.toFixed(2)}</p>
                        </div>
                      )}

                      {/* Accept/Decline Buttons */}
                      {sub.status === 'offer_made' && sub.counter_offer && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => respondToOffer(sub.id, 'accept')}
                            disabled={responding === `${sub.id}-main`}
                            className="flex-1 py-2 bg-green-500 text-black font-mono text-xs font-bold disabled:opacity-50"
                          >
                            ACCEPT ${sub.counter_offer.toFixed(0)}
                          </button>
                          <button
                            onClick={() => respondToOffer(sub.id, 'decline')}
                            disabled={responding === `${sub.id}-main`}
                            className="flex-1 py-2 bg-red-500 text-white font-mono text-xs font-bold disabled:opacity-50"
                          >
                            DECLINE
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Sell Items */
                    <div className="space-y-2">
                      {sub.items?.map(item => (
                        <div key={item.item_id} className="bg-zinc-800 p-2">
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-xs font-mono uppercase">{item.name}</p>
                                {renderStatusBadge(item.status)}
                                {item.status === 'accepted' && (
                                  <span
                                    className={`px-1.5 py-0.5 text-[9px] font-mono font-bold border ${item.received ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400'}`}
                                    title={item.received_at ? new Date(item.received_at).toLocaleString() : ''}
                                  >
                                    {item.received
                                      ? `✓ RECEIVED ${item.received_at ? '· ' + new Date(item.received_at).toLocaleDateString(undefined,{month:'short',day:'numeric'}) : ''}`
                                      : 'PENDING RECEIVING'}
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] text-zinc-400 font-mono mt-0.5">{item.description}</p>
                              <p className="text-[10px] text-teal-400 font-mono mt-0.5">
                                YOUR ASKING: ${item.asking_price?.toFixed(2)}
                              </p>
                              
                              {item.images?.length > 0 && (
                                <div className="flex gap-1 mt-1">
                                  {item.images.map((img, idx) => (
                                    <img 
                                      key={idx}
                                      src={getImageUrl(img)}
                                      alt=""
                                      className="w-8 h-8 object-cover cursor-pointer hover:opacity-80"
                                      onClick={() => setLightboxData({ images: item.images, initialIndex: idx })}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Counter Offer */}
                          {item.counter_offer && (
                            <div className="bg-yellow-500/20 border border-yellow-500/50 p-2 mt-2">
                              <p className="text-[10px] text-yellow-400 font-mono">THEIR OFFER TO BUY</p>
                              <p className="text-base font-bold font-mono">${item.counter_offer.toFixed(2)}</p>
                            </div>
                          )}

                          {/* Accept/Decline for this item */}
                          {item.status === 'offer_made' && item.counter_offer && (
                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={() => respondToOffer(sub.id, 'accept', item.item_id)}
                                disabled={responding === `${sub.id}-${item.item_id}`}
                                className="flex-1 py-1.5 bg-green-500 text-black font-mono text-xs font-bold disabled:opacity-50"
                              >
                                SELL FOR ${item.counter_offer.toFixed(0)}
                              </button>
                              <button
                                onClick={() => respondToOffer(sub.id, 'decline', item.item_id)}
                                disabled={responding === `${sub.id}-${item.item_id}`}
                                className="flex-1 py-1.5 bg-red-500 text-white font-mono text-xs font-bold disabled:opacity-50"
                              >
                                DECLINE
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {sub.notes && (
                    <p className="text-[10px] text-zinc-500 font-mono">NOTE: {sub.notes}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MySubmissionsSection;
